import React, { useEffect, useRef } from 'react';

import Button from 'components/partials/button/button';
import Checkbox from 'components/partials/checkbox/checkbox';
import RadioInput from 'components/partials/radio-input/radio-input';

import PopulationCriteriaBlock from './population-criteria-block';

import Icons from 'assets/icons';

import {
  BASELINE_POPULATIONS,
  POPULATION_TYPES,
} from '../matrix-step.constants';

import type { PopulationType } from '../matrix-step.constants';
import type {
  PopulationCriteriaBlock as CriteriaBlock,
  PopulationCriteriaBlockErrors,
  SelectOption,
} from '../hooks/use-populations-draft';

type PopulationsTabContentProps = {
  populationType: PopulationType;
  onPopulationTypeChange: (type: PopulationType) => void;
  baselineSelected: string | null;
  toggleBaseline: (value: string) => void;
  criteriaBlocks: CriteriaBlock[];
  criteriaErrors: Record<string, PopulationCriteriaBlockErrors>;
  canAddCriteriaBlock: boolean;
  getDimensionOptionsForBlock: (blockId: string) => SelectOption[];
  getValueOptions: (dimension: string | null) => SelectOption[];
  onAddCriteriaBlock: () => void;
  onRemoveCriteriaBlock: (blockId: string) => void;
  onCriteriaDimensionChange: (
    blockId: string,
    dimension: string | null
  ) => void;
  onCriteriaValuesChange: (blockId: string, values: string[]) => void;
  readOnly?: boolean;
  pellSelected: boolean;
};

const PopulationsTabContent = ({
  populationType,
  onPopulationTypeChange,
  baselineSelected,
  toggleBaseline,
  criteriaBlocks,
  criteriaErrors,
  canAddCriteriaBlock,
  getDimensionOptionsForBlock,
  getValueOptions,
  onAddCriteriaBlock,
  onRemoveCriteriaBlock,
  onCriteriaDimensionChange,
  onCriteriaValuesChange,
  readOnly,
  pellSelected,
}: PopulationsTabContentProps) => {
  const isBaseline = populationType === POPULATION_TYPES.BASELINE;

  const criteriaListRef = useRef<HTMLDivElement>(null);
  const previousBlocksCountRef = useRef(criteriaBlocks.length);

  useEffect(() => {
    if (criteriaBlocks.length > previousBlocksCountRef.current) {
      criteriaListRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'end',
      });
    }

    previousBlocksCountRef.current = criteriaBlocks.length;
  }, [criteriaBlocks.length]);

  return (
    <div
      className="relative p-3"
      data-testid="populations-tab-content"
      inert={readOnly}
    >
      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
        <legend className="sr-only">Population type</legend>
        <div className="flex flex-col gap-2">
          <RadioInput
            labelFor="pop-type-baseline"
            label="Based on baseline populations"
            radioValue={POPULATION_TYPES.BASELINE}
            value={populationType}
            name="population-type"
            onChange={(value) => {
              if (value) onPopulationTypeChange(value as PopulationType);
            }}
            customClass="!mb-0"
            customInputClasses="size-5"
          />
          <fieldset className="m-0 flex gap-4 border-0 p-0 pl-7">
            <legend className="sr-only">Baseline population</legend>
            {BASELINE_POPULATIONS.map((population) => (
              <RadioInput
                key={population.value}
                labelFor={`baseline-${population.value}`}
                label={population.label}
                radioValue={population.value}
                value={baselineSelected ?? ''}
                name="baseline-population"
                disabled={!isBaseline}
                onChange={(value) => {
                  if (value) toggleBaseline(value);
                }}
                customClass="!mb-0"
                customInputClasses="size-5"
              />
            ))}
          </fieldset>
        </div>

        <RadioInput
          labelFor="pop-type-custom"
          label="Custom populations only"
          radioValue={POPULATION_TYPES.CUSTOM}
          value={populationType}
          name="population-type"
          onChange={(value) => {
            if (value) onPopulationTypeChange(value as PopulationType);
          }}
          customClass="!mb-0"
          customInputClasses="size-5"
        />
      </fieldset>

      <Checkbox
        labelFor="pell-recipients"
        label="Pell Recipients"
        checkboxValue="pell"
        checked={pellSelected}
        disabled
        inputCustomClass="size-5"
        customStylesLabel="mt-4"
      />

      <div
        ref={criteriaListRef}
        className="mt-4 flex max-w-[434px] scroll-mb-3 flex-col gap-4"
      >
        {criteriaBlocks.map((block) => (
          <PopulationCriteriaBlock
            key={block.blockId}
            block={block}
            errors={criteriaErrors[block.blockId]}
            dimensionOptions={getDimensionOptionsForBlock(block.blockId)}
            valueOptions={getValueOptions(block.dimension)}
            onDimensionChange={onCriteriaDimensionChange}
            onValuesChange={onCriteriaValuesChange}
            onRemove={onRemoveCriteriaBlock}
          />
        ))}

        <Button
          type="button"
          variant="reserve-outlined"
          size="sm"
          className="w-fit"
          iconBefore={<Icons.Plus />}
          disabled={!canAddCriteriaBlock}
          onClick={onAddCriteriaBlock}
          data-testid="add-population-criteria-button"
        >
          More criteria
        </Button>
      </div>
    </div>
  );
};

export default PopulationsTabContent;
